/**
 * Cliente API del dashboard de Tesoreria (Sprint 17 Fase 2).
 *
 * Endpoints servidos por /tesoreria/dashboard/*:
 * - /resumen (D3 + tendencias %).
 * - /sparklines (3 series mensuales del año).
 * - /comparativa-anual (12 meses Ene-Dic).
 * - /gastos-por-categoria (donut data del mes).
 * - /actividad-reciente (feed unificado).
 */
import { apiFetch } from './client';

export interface ContadorMonto {
  count: number;
  monto_total: number;
}

export interface CasaAtrasadaResumen {
  lote_id: string;
  identificador: string;
  monto_adeudado: number;
  // C7: enriquecido para la lista del dashboard.
  titular: string | null;
  ultimo_pago_fecha: string | null; // YYYY-MM-DD
  estado: string; // atrasada | suspendida | al_dia_pendiente_semestre
}

export interface DashboardTesoreriaResumen {
  anio_activo: number; // año contable abierto (deriva de CierreAnual)
  mes_actual: string; // YYYY-MM-DD del primer dia del mes en curso
  recaudacion_ytd: number; // acumulado 1-ene → hoy del año activo
  gastos_ytd: number;
  saldo_neto_ytd: number;
  saldo_bancario: number;
  saldo_bancario_disponible: boolean;
  // Capital en depósitos a plazo vigentes (fuera de la cuenta corriente).
  total_depositos: number;
  tendencia_recaudacion_pct: number | null;
  tendencia_gastos_pct: number | null;
  tendencia_saldo_pct: number | null;
  // C4: variación % de casas en mora (vencidas) vs fin del mes anterior.
  tendencia_casas_contra_pct: number | null;
  pagos_sin_clasificar: ContadorMonto;
  casas_saldo_contra: ContadorMonto;
  casas_atrasadas_top5: CasaAtrasadaResumen[];
}

export interface PuntoSparkline {
  mes: string; // YYYY-MM-01
  monto: number;
}

export interface SparklinesResponse {
  anio: number;
  saldo_bancario: PuntoSparkline[];
  recaudacion: PuntoSparkline[];
  gastos: PuntoSparkline[];
}

export interface PuntoComparativaMensual {
  mes: string;
  recaudacion: number;
  gastos: number;
  es_futuro: boolean;
  es_parcial: boolean;
}

export interface ComparativaAnualResponse {
  anio: number;
  meses: PuntoComparativaMensual[];
}

export interface GastoPorSubcategoria {
  categoria_id: string;
  categoria_nombre: string;
  categoria_color: string;
  monto: number;
}

export interface GastoPorCategoria {
  categoria_id: string;
  categoria_nombre: string;
  categoria_color: string;
  monto: number;
  hijos: GastoPorSubcategoria[];
}

export interface GastosPorCategoriaResponse {
  mes: string;
  total: number;
  items: GastoPorCategoria[];
}

export interface EventoActividad {
  tipo:
    | 'pago_aprobado'
    | 'gasto_registrado'
    | 'compensacion_aplicada'
    | 'suspension'
    | 'cierre_firmado';
  timestamp: string;
  descripcion: string;
  monto: number | null;
  lote_identificador: string | null;
  entidad_id: string;
  enlace: string | null;
}

export interface ActividadRecienteResponse {
  eventos: EventoActividad[];
}

export function getDashboardResumen(): Promise<DashboardTesoreriaResumen> {
  return apiFetch<DashboardTesoreriaResumen>('/tesoreria/dashboard/resumen');
}

export function getSparklines(anio?: number): Promise<SparklinesResponse> {
  const qs = anio ? `?anio=${anio}` : '';
  return apiFetch<SparklinesResponse>(`/tesoreria/dashboard/sparklines${qs}`);
}

export function getComparativaAnual(
  anio?: number,
  incluirCompensaciones?: boolean,
): Promise<ComparativaAnualResponse> {
  const params = new URLSearchParams();
  if (anio) params.set('anio', String(anio));
  // Solo se envía cuando es false; el backend asume true (transparencia).
  if (incluirCompensaciones === false)
    params.set('incluir_compensaciones', 'false');
  const qs = params.toString();
  return apiFetch<ComparativaAnualResponse>(
    `/tesoreria/dashboard/comparativa-anual${qs ? `?${qs}` : ''}`,
  );
}

/**
 * Donut de gastos por categoría (C6). Alcance:
 * - `{ mes: 'YYYY-MM' }` → solo ese mes.
 * - `{ anio: YYYY }` → YTD de ese año.
 * - sin args → YTD del año contable activo (default "Año" del toggle).
 */
export function getGastosPorCategoria(
  scope: {
    mes?: string;
    anio?: number;
    incluirCompensaciones?: boolean;
  } = {},
): Promise<GastosPorCategoriaResponse> {
  const params = new URLSearchParams();
  if (scope.mes) params.set('mes', scope.mes);
  else if (scope.anio) params.set('anio', String(scope.anio));
  if (scope.incluirCompensaciones === false)
    params.set('incluir_compensaciones', 'false');
  const qs = params.toString();
  return apiFetch<GastosPorCategoriaResponse>(
    `/tesoreria/dashboard/gastos-por-categoria${qs ? `?${qs}` : ''}`,
  );
}

export function getActividadReciente(
  limit = 10,
): Promise<ActividadRecienteResponse> {
  return apiFetch<ActividadRecienteResponse>(
    `/tesoreria/dashboard/actividad-reciente?limit=${limit}`,
  );
}
